import { useEffect, useState, useCallback } from "react";
import { API } from "@/lib/backend-url";
import axios from "axios";
import { toast } from "sonner";
import { Vault, Coins, Calculator, ArrowLeftRight, Plus, X, History, Loader2 } from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const TYPE_LABEL = { cofre: "Cofre", caixinha: "Caixinha", caixa: "Caixa Registradora" };
const TYPE_ICON = { cofre: Vault, caixinha: Coins, caixa: Calculator };
const TYPE_COLORS = {
  cofre: "from-slate-700 to-slate-900 text-white",
  caixinha: "from-amber-500 to-amber-700 text-white",
  caixa: "from-emerald-600 to-emerald-800 text-white",
};

function fmtBRL(v) {
  return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);
}

function DenominationGrid({ denominations, quantities, onChange, readOnly = false }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
      {denominations.map((d) => {
        const q = quantities[d.key] || 0;
        const subtotal = q * d.value;
        return (
          <div key={d.key} className="border border-slate-200 rounded-lg p-3 bg-slate-50">
            <div className="flex items-baseline justify-between">
              <span className="text-sm font-semibold text-slate-700">{d.label}</span>
              <Badge variant="outline" className="text-[10px]">{d.type}</Badge>
            </div>
            {readOnly ? (
              <div className="mt-2">
                <div className="text-2xl font-bold text-slate-900">{q}</div>
                <div className="text-xs text-slate-500">{fmtBRL(subtotal)}</div>
              </div>
            ) : (
              <div className="mt-2 space-y-1">
                <Input
                  type="number"
                  min="0"
                  value={q}
                  onChange={(e) => onChange(d.key, Math.max(0, parseInt(e.target.value || "0", 10)))}
                  className="h-9 text-base"
                  data-testid={`denom-input-${d.key}`}
                />
                <div className="text-xs text-slate-500">= {fmtBRL(subtotal)}</div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function CashControlPage() {
  const [denominations, setDenominations] = useState([]);
  const [locations, setLocations] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [storeFilter, setStoreFilter] = useState("all");
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [movements, setMovements] = useState([]);
  const [movementsLoading, setMovementsLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, l, s] = await Promise.all([
        axios.get(`${API}/cash/denominations`),
        axios.get(`${API}/cash/locations`),
        axios.get(`${API}/cash/balance/summary`),
      ]);
      setDenominations(d.data.data);
      setLocations(l.data.data);
      setSummary(s.data.data);
    } catch (e) {
      toast.error("Erro ao carregar dados do caixa");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = locations.filter((l) => storeFilter === "all" || l.store_id === storeFilter);
  const stores = Array.from(new Set(locations.map((l) => ({ id: l.store_id, name: l.store_name })).map((s) => JSON.stringify(s))))
    .map((s) => JSON.parse(s));

  const openHistory = async () => {
    setHistoryOpen(true);
    setMovementsLoading(true);
    try {
      const r = await axios.get(`${API}/cash/movements`, { params: { limit: 50 } });
      setMovements(r.data.data);
    } catch {
      toast.error("Erro ao carregar histórico");
    } finally {
      setMovementsLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-10 h-10 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="cash-control-page">
      {/* Top summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-slate-700 to-slate-900 text-white">
          <CardContent className="p-5">
            <div className="text-xs uppercase tracking-wider opacity-70">Total Empresa</div>
            <div className="text-3xl font-bold mt-1">{fmtBRL(summary?.total)}</div>
            <div className="text-xs opacity-70 mt-2">Soma de todos os locais</div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-slate-600 to-slate-800 text-white">
          <CardContent className="p-5">
            <Vault className="w-5 h-5 opacity-70" />
            <div className="text-xs uppercase opacity-70 mt-2">Cofres</div>
            <div className="text-2xl font-bold mt-1">{fmtBRL(summary?.by_type?.cofre)}</div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-amber-500 to-amber-700 text-white">
          <CardContent className="p-5">
            <Coins className="w-5 h-5 opacity-90" />
            <div className="text-xs uppercase opacity-90 mt-2">Caixinhas</div>
            <div className="text-2xl font-bold mt-1">{fmtBRL(summary?.by_type?.caixinha)}</div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-emerald-600 to-emerald-800 text-white">
          <CardContent className="p-5">
            <Calculator className="w-5 h-5 opacity-90" />
            <div className="text-xs uppercase opacity-90 mt-2">Caixas Registradoras</div>
            <div className="text-2xl font-bold mt-1">{fmtBRL(summary?.by_type?.caixa)}</div>
          </CardContent>
        </Card>
      </div>

      {/* Controls */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3 flex-wrap">
          <Select value={storeFilter} onValueChange={setStoreFilter}>
            <SelectTrigger className="w-[240px]" data-testid="store-filter-select">
              <SelectValue placeholder="Filtrar por loja" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas as lojas</SelectItem>
              {stores.map((s) => (
                <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={openHistory} data-testid="open-history-btn">
            <History className="w-4 h-4 mr-2" />Histórico
          </Button>
          <Button variant="outline" onClick={() => setTransferOpen(true)} data-testid="open-transfer-btn">
            <ArrowLeftRight className="w-4 h-4 mr-2" />Transferir
          </Button>
          <Button onClick={() => setAdjustOpen(true)} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="open-adjust-btn">
            <Plus className="w-4 h-4 mr-2" />Ajustar Saldo
          </Button>
        </div>
      </div>

      {/* Location cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map((loc) => {
          const Icon = TYPE_ICON[loc.type] || Calculator;
          return (
            <Card key={loc.id} className="overflow-hidden" data-testid={`location-card-${loc.id}`}>
              <CardHeader className={`bg-gradient-to-br ${TYPE_COLORS[loc.type] || "from-slate-600 to-slate-800 text-white"} p-4`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Icon className="w-5 h-5" />
                    <CardTitle className="text-base">{loc.name}</CardTitle>
                  </div>
                  <Badge className="bg-white/20 text-white text-[10px]">{TYPE_LABEL[loc.type]}</Badge>
                </div>
                <div className="text-xs opacity-80">{loc.store_name}</div>
                <div className="text-2xl font-bold mt-2">{fmtBRL(loc.total)}</div>
              </CardHeader>
              <CardContent className="p-4">
                <div className="text-xs text-slate-500 mb-2">Composição</div>
                <div className="grid grid-cols-3 gap-1.5 text-xs">
                  {denominations.map((d) => {
                    const q = (loc.balance || {})[d.key] || 0;
                    return (
                      <div key={d.key} className="bg-slate-50 rounded px-2 py-1 flex items-center justify-between border border-slate-100">
                        <span className="text-slate-600">{d.label}</span>
                        <span className={`font-semibold ${q > 0 ? "text-slate-900" : "text-slate-300"}`}>{q}</span>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <AdjustModal
        open={adjustOpen}
        onOpenChange={setAdjustOpen}
        locations={locations}
        denominations={denominations}
        onSaved={load}
      />
      <TransferModal
        open={transferOpen}
        onOpenChange={setTransferOpen}
        locations={locations}
        denominations={denominations}
        onSaved={load}
      />
      <HistoryModal
        open={historyOpen}
        onOpenChange={setHistoryOpen}
        movements={movements}
        loading={movementsLoading}
      />
    </div>
  );
}


function AdjustModal({ open, onOpenChange, locations, denominations, onSaved }) {
  const [locationId, setLocationId] = useState("");
  const [quantities, setQuantities] = useState({});
  const [operatorName, setOperatorName] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) {
      setLocationId("");
      setQuantities({});
      setOperatorName("");
      setNote("");
    }
  }, [open]);

  useEffect(() => {
    if (!locationId) return;
    const loc = locations.find((l) => l.id === locationId);
    setQuantities(loc?.balance || {});
  }, [locationId, locations]);

  const total = denominations.reduce((s, d) => s + (quantities[d.key] || 0) * d.value, 0);

  const save = async () => {
    if (!locationId) return toast.error("Selecione um local");
    setSaving(true);
    try {
      await axios.post(`${API}/cash/adjust`, {
        location_id: locationId,
        quantities,
        operator_name: operatorName || null,
        note: note || null,
      });
      toast.success("Saldo ajustado!");
      onSaved && onSaved();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao ajustar saldo");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="adjust-modal">
        <DialogHeader>
          <DialogTitle>Ajustar Saldo</DialogTitle>
          <DialogDescription>Informe a contagem absoluta de cada cédula/moeda no local.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Local</Label>
            <Select value={locationId} onValueChange={setLocationId}>
              <SelectTrigger data-testid="adjust-location-select">
                <SelectValue placeholder="Selecione o local" />
              </SelectTrigger>
              <SelectContent>
                {locations.map((l) => (
                  <SelectItem key={l.id} value={l.id}>{l.store_name} — {l.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DenominationGrid
            denominations={denominations}
            quantities={quantities}
            onChange={(k, v) => setQuantities((p) => ({ ...p, [k]: v }))}
          />
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Conferente</Label>
              <Input value={operatorName} onChange={(e) => setOperatorName(e.target.value)} placeholder="Nome do operador" />
            </div>
            <div>
              <Label>Observação</Label>
              <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Opcional" />
            </div>
          </div>
          <div className="flex items-center justify-between p-4 bg-emerald-50 rounded-lg border border-emerald-200">
            <span className="text-sm text-slate-700">Total após ajuste:</span>
            <span className="text-2xl font-bold text-emerald-700" data-testid="adjust-total">{fmtBRL(total)}</span>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button disabled={saving || !locationId} onClick={save} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="adjust-save-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Salvar
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}


function TransferModal({ open, onOpenChange, locations, denominations, onSaved }) {
  const [fromId, setFromId] = useState("");
  const [toId, setToId] = useState("");
  const [quantities, setQuantities] = useState({});
  const [operatorName, setOperatorName] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) {
      setFromId(""); setToId(""); setQuantities({}); setOperatorName(""); setReason("");
    }
  }, [open]);

  const fromLoc = locations.find((l) => l.id === fromId);
  const total = denominations.reduce((s, d) => s + (quantities[d.key] || 0) * d.value, 0);

  const save = async () => {
    if (!fromId || !toId) return toast.error("Selecione origem e destino");
    if (fromId === toId) return toast.error("Origem e destino devem ser diferentes");
    if (total <= 0) return toast.error("Informe ao menos uma denominação");
    setSaving(true);
    try {
      await axios.post(`${API}/cash/transfer`, {
        from_location_id: fromId,
        to_location_id: toId,
        quantities,
        operator_name: operatorName || null,
        reason: reason || null,
      });
      toast.success(`Transferido ${fmtBRL(total)}`);
      onSaved && onSaved();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro na transferência");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="transfer-modal">
        <DialogHeader>
          <DialogTitle>Transferir Dinheiro</DialogTitle>
          <DialogDescription>Mover cédulas/moedas entre locais (sem assinatura, fluxo simples).</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>De</Label>
              <Select value={fromId} onValueChange={setFromId}>
                <SelectTrigger data-testid="transfer-from-select">
                  <SelectValue placeholder="Origem" />
                </SelectTrigger>
                <SelectContent>
                  {locations.map((l) => (
                    <SelectItem key={l.id} value={l.id}>{l.store_name} — {l.name} ({fmtBRL(l.total)})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Para</Label>
              <Select value={toId} onValueChange={setToId}>
                <SelectTrigger data-testid="transfer-to-select">
                  <SelectValue placeholder="Destino" />
                </SelectTrigger>
                <SelectContent>
                  {locations.map((l) => (
                    <SelectItem key={l.id} value={l.id} disabled={l.id === fromId}>
                      {l.store_name} — {l.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          {fromLoc && (
            <div className="text-xs text-slate-500">
              Saldo disponível: {denominations.map((d) => `${d.label}: ${(fromLoc.balance || {})[d.key] || 0}`).join(" • ")}
            </div>
          )}
          <DenominationGrid
            denominations={denominations}
            quantities={quantities}
            onChange={(k, v) => setQuantities((p) => ({ ...p, [k]: v }))}
          />
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Conferente</Label>
              <Input value={operatorName} onChange={(e) => setOperatorName(e.target.value)} />
            </div>
            <div>
              <Label>Motivo</Label>
              <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Ex: Abastecimento de troco" />
            </div>
          </div>
          <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg border border-blue-200">
            <span className="text-sm text-slate-700">Total a transferir:</span>
            <span className="text-2xl font-bold text-blue-700" data-testid="transfer-total">{fmtBRL(total)}</span>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button disabled={saving || !fromId || !toId || total <= 0} onClick={save} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="transfer-save-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Transferir
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}


function HistoryModal({ open, onOpenChange, movements, loading }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="history-modal">
        <DialogHeader>
          <DialogTitle>Histórico de Movimentações</DialogTitle>
          <DialogDescription>Últimas 50 operações de caixa</DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="flex justify-center py-10"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>
        ) : movements.length === 0 ? (
          <div className="text-center py-10 text-slate-500">Nenhuma movimentação registrada</div>
        ) : (
          <div className="space-y-2">
            {movements.map((m) => (
              <div key={m.id} className="p-3 border border-slate-200 rounded-lg flex items-center justify-between hover:bg-slate-50">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <Badge variant={m.type === "transferencia" ? "default" : "secondary"} className="text-[10px]">
                      {m.type.toUpperCase()}
                    </Badge>
                    <span className="text-sm font-medium text-slate-800">
                      {m.type === "transferencia"
                        ? `${m.from_location_name} → ${m.to_location_name}`
                        : m.location_name}
                    </span>
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {new Date(m.timestamp).toLocaleString("pt-BR")}
                    {m.operator_name ? ` • ${m.operator_name}` : ""}
                    {m.reason ? ` • ${m.reason}` : ""}
                    {m.note ? ` • ${m.note}` : ""}
                  </div>
                </div>
                <div className={`text-lg font-bold ${m.amount >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                  {m.amount >= 0 ? "+" : ""}{fmtBRL(m.amount)}
                </div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
